/**
 * useTranscript Hook
 *
 * React Query hook for fetching and managing transcript data including
 * segments and speaker information for a recording.
 */

import { useQuery } from "@tanstack/react-query";
import { getTranscriptWithSegments, getSpeakers } from "@/services/transcripts";
import type {
  TranscriptWithSegmentsResponse,
  TranscriptSegmentResponse,
  SpeakerResponse,
} from "@/types/transcript";

interface UseTranscriptOptions {
  enabled?: boolean;
}

interface UseTranscriptResult {
  transcript: TranscriptWithSegmentsResponse | null;
  segments: TranscriptSegmentResponse[];
  speakers: SpeakerResponse[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useTranscript(
  recordingId: string | undefined,
  options: UseTranscriptOptions = {}
): UseTranscriptResult {
  const { enabled = true } = options;
  // Fetch transcript with segments
  const {
    data: transcript,
    isLoading: transcriptLoading,
    isFetching: transcriptFetching,
    error: transcriptError,
    refetch: refetchTranscript,
  } = useQuery<TranscriptWithSegmentsResponse, Error>({
    queryKey: ["transcript", recordingId],
    queryFn: () => getTranscriptWithSegments(recordingId!),
    enabled: !!recordingId && enabled,
    retry: (failureCount, error) => {
      // Don't retry on 404 (transcript not found)
      if (
        (error as { response?: { status?: number } })?.response?.status === 404
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Fetch speakers
  const {
    data: speakers,
    isLoading: speakersLoading,
    isFetching: speakersFetching,
    error: speakersError,
    refetch: refetchSpeakers,
  } = useQuery<SpeakerResponse[], Error>({
    queryKey: ["speakers", recordingId],
    queryFn: () => getSpeakers(recordingId!),
    enabled: !!recordingId && enabled,
    retry: (failureCount, error) => {
      // Don't retry on 404
      if (
        (error as { response?: { status?: number } })?.response?.status === 404
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const refetch = () => {
    refetchTranscript();
    refetchSpeakers();
  };

  // Combine loading states
  const isLoading = transcriptLoading || speakersLoading;

  // Combine fetching states (true during initial load AND during refetch)
  const isFetching = transcriptFetching || speakersFetching;

  // Use first non-null error
  const error = transcriptError || speakersError || null;

  return {
    transcript: transcript || null,
    segments: transcript?.segments || [],
    speakers: speakers || [],
    isLoading,
    isFetching,
    error,
    refetch,
  };
}
